import { Component } from 'react'
import { IClientUID } from '../../../api/clients'
import { getClientSystemInfo, ISystemInfo } from '../../../api/0x0001/systeminfo'
import { ProgramMode } from '../../../api/0x0001/settings'
import { toast } from 'react-toastify'
import Loader from '../../../components/loader'
import { mvToTemp } from '../../../utils/ntc'
import { mvToBar } from '../../../utils/piezo'
import { mvToPercent } from '../../../utils/number'
import { readableTime } from '../../../utils/time'
import { getLocale, getString } from '../../../locale'
import classnames from 'classnames'
import { format } from 'date-fns'
import { readableMemory } from '../../../utils/memory'

export enum PumpStatus {
    'Off' = 0,
    'On' = 1,
    'Timed Out' = 3,
    'Insufficient Pressure' = 4,
}

export const PumpState: Record<string | number, any> = {
    '0': <i className="flex items-center justify-center w-5 h-5 rounded-full bg-red-500" />,
    '1': <i className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500" />,
    '3': <i className="flex items-center justify-center w-5 h-5 rounded-full bg-yellow-500" />,
    '4': <i className="flex items-center justify-center w-5 h-5 rounded-full bg-yellow-500" />,
}

export const RegularState: Record<string | number, any> = {
    '0': <i className="flex items-center justify-center w-5 h-5 rounded-full bg-red-500" />,
    '1': <i className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500" />,
}

export const RegularStateFunc: Record<string | number, (title: string) => any> = {
    '0': (title) => <i className="flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-black dark:text-white">{title}</i>,
    '1': (title) => <i className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500 text-black dark:text-white">{title}</i>,
}

export type IntervalOption = { label: () => string; ms: number }
export const INTERVAL_OPTIONS: Array<IntervalOption> = [
    {
        label: () => getString('time.half_second'),
        ms: 500,
    },
    {
        label: () => `2 ${getString('time.seconds')}`,
        ms: 2000,
    },
]

type Props = {
    clientId: IClientUID
    intervalOption?: number
}

type State = {
    systemInfo?: ISystemInfo
    intervalOption: IntervalOption
}

class SystemInfoTab extends Component<Props, State> {
    mounted: boolean = false

    state: State = {
        systemInfo: undefined,
        intervalOption: INTERVAL_OPTIONS[this.props.intervalOption === undefined ? 1 : this.props.intervalOption],
    }

    setState: Component<Props, State>['setState'] = (state, callback) => {
        if (!this.mounted) return
        return super.setState(state as State, callback)
    }

    componentDidMount = async () => {
        this.mounted = true
        try {
            const systemInfo = (await getClientSystemInfo(this.props.clientId)).data
            console.log('systemInfo', systemInfo)

            this.setState({ systemInfo })
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
        setTimeout(this.loadData.bind(this), this.state.intervalOption.ms)
    }

    componentWillUnmount = () => {
        this.mounted = false
    }

    async loadData() {
        if (!this.mounted) return
        try {
            const systemInfo = (await getClientSystemInfo(this.props.clientId)).data
            console.log('systemInfo', systemInfo)
            this.setState({ systemInfo })
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
        setTimeout(this.loadData.bind(this), this.state.intervalOption.ms)
    }

    render() {
        return (
            <>
                {this.renderTable()}
                {this.renderIntervalSelect()}
            </>
        )
    }

    renderIntervalSelect() {
        const { intervalOption } = this.state
        return (
            <div className="grid grid-cols-3 gap-8">
                <div className="flex items-center dark:text-white">{getString('system_info.update_interval')}:</div>
                {INTERVAL_OPTIONS.map((option, i) => (
                    <div
                        key={i}
                        onClick={() => this.setState({ intervalOption: option })}
                        className={classnames(
                            'flex items-center p-4 bg-white dark:bg-gray-700 dark:text-white shadow-md rounded-xl',
                            intervalOption.ms === option.ms ? 'bg-gray-200 dark:bg-opacity-40 cursor-not-allowed' : 'hover-grow',
                        )}
                    >
                        {option.label()}
                    </div>
                ))}
            </div>
        )
    }

    renderTable() {
        const { systemInfo } = this.state
        if (systemInfo) {
            console.log('Number(systemInfo.epoch) * 1000', Number(systemInfo.epoch) * 1000)
        }
        return (
            // <table className="bg-white dark:bg-gray-800 dark:text-white w-full rounded-xl shadow-md table-striped mt-8">
            // <tbody>
            <div className="mb-8 bg-white dark:bg-gray-800 shadow-md rounded-xl flex flex-wrap justify-center items-stretch w-full p-4">
                {this.renderRow(
                    getString('system_info.epoch'),
                    systemInfo && (
                        <>
                            <div style={{ direction: 'ltr' }}>{format(Number(systemInfo.epoch) * 1000, 'dd/MM/yyyy HH:mm:ss')}</div>
                            <div>
                                <div className="p-2 pb-0 text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.uptime')}</div>
                                {readableTime(Number(systemInfo.uptime))}
                            </div>
                            <div>
                                <div className="p-2 pb-0 text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.free_heap')}</div>
                                {readableMemory(Number(systemInfo.freeHeap))}
                            </div>
                            <div className="mt-3 flex justify-center space-x-5">
                                <div>
                                    <div className="text-base text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.vdet')}</div>
                                    <div className="text-xs">
                                        {systemInfo.vdet / 1000}
                                        {getString('common.v')}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-base text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.iprop')}</div>
                                    <div className="text-xs">
                                        {systemInfo.iprop / 1000}
                                        {getString('common.v')}
                                    </div>
                                </div>
                            </div>
                        </>
                    ),
                    undefined,
                    undefined,
                    'base',
                )}

                {(!systemInfo?.ignoreH2oPiezo || !systemInfo?.ignoreCo2Piezo) && this.renderRow(
                    getString('system_info.piezo1'),
                    systemInfo && (
                        <>
                            <div className={classnames(systemInfo.ignoreH2oPiezo && 'line-through')}>
                                {systemInfo.piezo1 < 3050 ?
                                    <div style={{ direction: getLocale() === 'he' ? 'rtl' : 'ltr' }}>
                                        {mvToBar(systemInfo.piezo1)} {getString('common.bar')}
                                    </div> :
                                    <div className="text-sm text-red-500">
                                        {getString('common.disconnected')}
                                    </div>
                                }
                                <div className="mt-3 text-xs">
                                    {systemInfo.piezo1 / 1000}
                                    {getString('common.v')}
                                </div>
                            </div>

                            <div className="mt-3 text-base text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.piezo2')}</div>
                            {systemInfo.piezo2 < 3050 ?
                                <div
                                    className={classnames('mt-3', systemInfo.ignoreCo2Piezo && 'line-through')}
                                    style={{ direction: getLocale() === 'he' ? 'rtl' : 'ltr' }}
                                >
                                    {mvToBar(systemInfo.piezo2)} {getString('common.bar')}
                                </div> :
                                <div className="mt-3 text-sm text-red-500">
                                    {getString('common.disconnected')}
                                </div>
                            }
                            <div className={classnames('mt-3 text-xs', systemInfo.ignoreCo2Piezo && 'line-through')}>
                                {systemInfo.piezo2 / 1000}
                                {getString('common.v')}
                            </div>
                        </>
                    ),
                    !!systemInfo,
                )}

                {systemInfo?.progMode === ProgramMode.IceBank &&
                    this.renderRow(
                        getString('system_info.ice_sense'),
                        <>
                            <div>{mvToPercent(systemInfo.iceSense)}%</div>
                            <div className="mt-3 text-xs">
                                {systemInfo.iceSense / 1000}
                                {getString('common.v')}
                            </div>

                            <div className="mt-3">
                                <div className="text-base text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.ice_sense')}</div>
                                <div className="mt-3">{mvToPercent(systemInfo.rawIceSense)}%</div>
                                <div className="mt-3 text-xs">
                                    {systemInfo.rawIceSense / 1000}
                                    {getString('common.v')}
                                </div>
                            </div>
                        </>,
                    )}

                {this.renderRow(
                    getString('system_info.cooling_unit'),
                    systemInfo && (
                        <>
                            <div className="flex flex-col items-center">{RegularState[Number(systemInfo.compressorState)]}</div>
                            <div className="mt-3">
                                <div className="text-sm text-gray-600 dark:text-gray-400 font-semibold">
                                    {getString('common.thermostat')}
                                </div>
                                <div className="mt-1" style={{ direction: 'ltr' }}>
                                    {systemInfo.ntc1 < 3050 ?
                                        <>
                                            {mvToTemp(systemInfo.ntc1)[0]} {getString('common.celsius')}
                                        </> :
                                        <div className="text-sm text-red-500">
                                            {getString('common.disconnected')}
                                        </div>
                                    }
                                </div>
                                <div className="mt-3 text-xs">
                                    {systemInfo.ntc1 / 1000}
                                    {getString('common.v')}
                                </div>
                            </div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">
                                    {getString('common.active_time')}
                                </div>
                                <div>{readableTime(systemInfo.compressorActiveTime)}</div>
                            </div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">
                                    {getString('common.inactive_time')}
                                </div>
                                <div>{readableTime(systemInfo.compressorInactiveTime)}</div>
                            </div>
                        </>
                    ),
                )}

                {!systemInfo?.liqLevDisabled &&
                    this.renderRow(
                        getString('system_info.soda_unit'),
                        systemInfo && (
                            <>
                                <div className="flex flex-col items-center">{PumpState[Number(systemInfo.pumpState)]}</div>
                                <div className="text-sm mt-3">
                                    <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.status')}</div>
                                    <div>{PumpStatus[systemInfo.pumpState]}</div>
                                </div>
                                <div className="text-sm mt-3">
                                    <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.active_time')}</div>
                                    <div>{readableTime(systemInfo.pumpActiveTime)}</div>
                                </div>
                                <div className="text-sm mt-3">
                                    <div className="text-gray-600 dark:text-gray-400 font-semibold">
                                        {getString('common.inactive_time')}
                                    </div>
                                    <div>{readableTime(systemInfo.pumpInactiveTime)}</div>
                                </div>

                                <div className="text-base mt-3">
                                    <div className="mb-2 text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.liquid_level')}</div>
                                    {!systemInfo.oneLiqLev && (
                                        <div className="mb-3 flex items-center justify-center">
                                            <div className="me-5 text-sm text-gray-600 dark:text-gray-400 font-semibold">
                                                {getString('system_info.liquid_high')}
                                            </div>
                                            <div>{RegularState[Number(systemInfo.liqH)]}</div>
                                        </div>
                                    )}
                                    <div className="flex flex-row justify-center items-center">
                                        {!systemInfo.oneLiqLev && (<>
                                            <div className="me-5 text-sm text-gray-600 dark:text-gray-400 font-semibold">
                                                {getString('system_info.liquid_low')}
                                            </div>
                                            <div>{RegularState[Number(systemInfo.liqL)]}</div>
                                        </>)}
                                    </div>
                                </div>
                            </>
                        ),
                    )}

                {systemInfo?.fanEnabled && this.renderRow(
                    getString('system_info.ambient_unit'),
                    systemInfo && (
                        <>
                            <div className="flex flex-col items-center">{RegularState[Number(systemInfo.fanState)]}</div>
                            <div className="mt-3">
                                <div className="text-sm text-gray-600 dark:text-gray-400 font-semibold">
                                    {getString('common.thermostat')}
                                </div>
                                <div className="mt-1" style={{ direction: 'ltr' }}>
                                    {systemInfo.ntc2 < 3050 ?
                                        <>
                                            {mvToTemp(systemInfo.ntc2)[0]} {getString('common.celsius')}
                                        </> :
                                        <div className="text-sm text-red-500">
                                            {getString('common.disconnected')}
                                        </div>
                                    }
                                </div>
                                <div className="mt-3 text-xs">
                                    {systemInfo.ntc2 / 1000}
                                    {getString('common.v')}
                                </div>
                            </div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">
                                    {getString('common.active_time')}
                                </div>
                                <div>{readableTime(systemInfo.fanActiveTime)}</div>
                            </div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">
                                    {getString('common.inactive_time')}
                                </div>
                                <div>{readableTime(systemInfo.fanInactiveTime)}</div>
                            </div>
                        </>
                    ),
                )}

                {systemInfo?.circPumpEnabled && this.renderRow(
                    getString('system_info.circulation_pump'),
                    systemInfo && (
                        <>
                            <div className="flex flex-col items-center">{RegularState[Number(systemInfo.circPumpState)]}</div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">
                                    {getString('common.active_time')}
                                </div>
                                <div>{readableTime(systemInfo.circPumpActiveTime)}</div>
                            </div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">
                                    {getString('common.inactive_time')}
                                </div>
                                <div>{readableTime(systemInfo.circPumpInactiveTime)}</div>
                            </div>
                        </>
                    ),
                )}

                {systemInfo?.boilerEnabled &&
                    this.renderRow(
                        getString('system_info.boiler_state'),
                        <>
                            <div className="flex flex-col items-center">{RegularState[Number(systemInfo.boilerState)]}</div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.boiler_active_time')}</div>
                                <div>{readableTime(systemInfo.boilerActiveTime)}</div>
                            </div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.boiler_inactive_time')}</div>
                                <div>{readableTime(systemInfo.boilerInactiveTime)}</div>
                            </div>
                        </>,
                    )}

                {this.renderRow(
                    getString('system_info.rel_sols'),
                    systemInfo && (
                        <>
                            <div className="text-sm mt-3 flex justify-center">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold ">{getString('system_info.rel_sol1_state')}:</div>
                                <div className="ms-1">{RegularState[Number(systemInfo.relSol1State)]}</div>
                            </div>
                            <div className="text-sm mt-3 flex justify-center">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold ">{getString('system_info.rel_sol2_state')}:</div>
                                <div className="ms-1">{RegularState[Number(systemInfo.relSol2State)]}</div>
                            </div>
                            <div className="text-sm mt-3 flex justify-center">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold ">{getString('system_info.rel_sol3_state')}:</div>
                                <div className="ms-1">{RegularState[Number(systemInfo.relSol3State)]}</div>
                            </div>
                            <div className="text-sm mt-3 flex justify-center">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.rel_sol4_state')}:</div>
                                <div className="ms-1">{RegularState[Number(systemInfo.relSol4State)]}</div>
                            </div>
                            <div className="text-sm mt-3 flex justify-center">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.rel_sol5_state')}:</div>
                                <div className="ms-1">{RegularState[Number(systemInfo.relSol5State)]}</div>
                            </div>
                            <div className="text-sm mt-3 flex justify-center">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.rel_sol6_state')}:</div>
                                <div className="ms-1">{RegularState[Number(systemInfo.relSol6State)]}</div>
                            </div>
                            <div className="text-sm mt-3 flex justify-center">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.rel_sol7_state')}:</div>
                                <div className="ms-1">{RegularState[Number(systemInfo.relSol7State)]}</div>
                            </div>
                            <div className="text-sm mt-3 flex justify-center">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.rel_sol8_state')}:</div>
                                <div className="ms-1">{RegularState[Number(systemInfo.relSol8State)]}</div>
                            </div>
                        </>
                    ),
                )}
            </div>
            // </tbody>
            // </table>
        )
    }

    renderRow(label: string, value: any, ltr = false, message?: any, size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl') {
        return (
            // <tr
            //     className={classnames(
            //         'first:rounded-t-xl last:rounded-b-xl even:bg-gray-200 dark:even:bg-gray-700',
            //         ltr && 'ltr',
            //         ltr && getLocale() === 'he' && 'text-start',
            //     )}
            // >
            //     <th className="p-4 text-start">{label}</th>
            //     <td className="p-4">
            //         {this.renderValue(value)} {message}
            //     </td>
            // </tr>
            <div className={classnames('w-56 text-center bg-gray-100 dark:bg-gray-900 m-1 flex flex-col items-center', ltr && 'ltr')}>
                <div className="p-2 pb-0 text-gray-600 dark:text-gray-400 font-semibold">{label}</div>
                <div className={classnames(`p-4 text-black dark:text-white font-bold text-${size || '2xl'} leading-7`)}>
                    {this.renderValue(value)} {message}
                </div>
            </div>
        )
    }

    renderValue(value: any) {
        const { systemInfo } = this.state
        if (systemInfo === undefined) return <Loader />
        return value
    }
}

export default SystemInfoTab
